import { LINEAR_ACCESSORS } from '../components/displays/index.js';
import {
  AREA_VARIANT,
  type AnyLinearSeries,
  type AnyXYChartScale,
  type AreaMetadata,
  type DotMetadata,
  type LineMetadata,
  type MapDatapoints,
} from '../types/xy-chart-internals.js';
import type {
  AreaDatapoint,
  DotDatapoint,
  LineDatapoint,
} from '../types/xy-chart.js';
import { resolveAreaAccessKey } from './resolve-area-accessor-key.js';
import {
  isHorizontalAreaAccessors,
  isVerticalAreaAccessors,
} from './type-guards.js';

export type AnyLinearDatapoint = DotDatapoint | LineDatapoint | AreaDatapoint;

export type AnyLinearAccessors = {
  xAccessor: (scale: AnyXYChartScale) => (d: AnyLinearDatapoint) => number;
  yAccessor: (scale: AnyXYChartScale) => (d: AnyLinearDatapoint) => number;
};

export type AnyLinearSeriesMetadata = AreaMetadata | DotMetadata | LineMetadata;

export function getLinearAccessors(
  seriesType: AnyLinearSeries['type'],
  metadata: AnyLinearSeriesMetadata,
  datapointToMetadata: MapDatapoints,
  xRelAccessor?: (value: number) => number,
  yRelAccessor?: (value: number) => number,
): AnyLinearAccessors {
  let accessKey: keyof typeof LINEAR_ACCESSORS;
  if (seriesType === AREA_VARIANT) {
    const { orientation, variant } = metadata as AreaMetadata;
    accessKey = resolveAreaAccessKey(variant, orientation);
  } else {
    accessKey = seriesType;
  }

  const pair = LINEAR_ACCESSORS[accessKey];

  if (isHorizontalAreaAccessors(pair)) {
    const { isStacked } = metadata as AreaMetadata;
    return {
      xAccessor: (scale: AnyXYChartScale) =>
        pair.x(scale, datapointToMetadata, xRelAccessor, isStacked),
      yAccessor: (scale: AnyXYChartScale) => pair.y(scale, yRelAccessor),
    };
  } else if (isVerticalAreaAccessors(pair)) {
    const { isStacked } = metadata as AreaMetadata;
    return {
      xAccessor: (scale: AnyXYChartScale) => pair.x(scale, xRelAccessor),
      yAccessor: (scale: AnyXYChartScale) =>
        pair.y(scale, datapointToMetadata, yRelAccessor, isStacked),
    };
  } else {
    return {
      xAccessor: (scale: AnyXYChartScale) =>
        pair.x(scale, xRelAccessor) as (
          datapoint: AnyLinearDatapoint,
        ) => number,
      yAccessor: (scale: AnyXYChartScale) =>
        pair.y(scale, yRelAccessor) as (
          datapoint: AnyLinearDatapoint,
        ) => number,
    };
  }
}
